import { useRef } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { useUploadWebsiteImage } from '../../services/httpServices/queries';

interface ImageUploadFieldProps {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  error?: string;
  helperText?: string;
  /** Tailwind height class for the preview area. Defaults to h-40. */
  previewClassName?: string;
}

/**
 * Upload-only image input with a live preview. There is no URL/path text
 * field — admins pick a file, it is optimized/stored, and its public URL is
 * set behind the scenes. Matches the WordPress "featured image" interaction.
 */
export function ImageUploadField({
  label,
  value,
  onChange,
  error,
  helperText,
  previewClassName = 'h-40',
}: ImageUploadFieldProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadWebsiteImage();

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    try {
      const res = await upload.mutateAsync(file);
      onChange(res.data.url);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-text mb-1">{label}</label>}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {value ? (
        <div className="space-y-2">
          <div className={`relative rounded-lg border border-border overflow-hidden bg-background ${previewClassName}`}>
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="px-3 py-1.5 rounded-lg border border-border text-text-muted hover:text-primary hover:border-primary transition-colors text-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {upload.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Replace
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="px-3 py-1.5 rounded-lg border border-border text-text-muted hover:text-danger hover:border-danger transition-colors text-sm flex items-center gap-1.5"
            >
              <X size={14} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className={`w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors ${previewClassName} ${
            error
              ? 'border-danger text-danger'
              : 'border-border text-text-muted hover:border-primary hover:text-primary'
          } disabled:opacity-50`}
        >
          {upload.isPending ? (
            <Loader2 size={22} className="animate-spin" />
          ) : (
            <Upload size={22} />
          )}
          <span className="text-sm font-medium">
            {upload.isPending ? 'Uploading…' : 'Click to upload image'}
          </span>
          <span className="text-xs">PNG, JPG or WEBP</span>
        </button>
      )}

      {helperText && !error && <p className="text-xs text-text-muted mt-1.5">{helperText}</p>}
      {error && <p className="text-xs text-danger mt-1.5">{error}</p>}
      {upload.isError && <p className="text-xs text-danger mt-1.5">Upload failed. Please try again.</p>}
    </div>
  );
}
